import { AuthService, REFRESH_OK, REFRESH_TERMINAL } from "services/authservice";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "../config/constants";
import { errorHandler } from "../utils/errorHandler";
import { rememberConnectReturn } from "../utils/connectReturn";
import i18n from "../i18n/index.js";

// Manejo compartido de 401 para los tres clientes axios (v1, v2 y legacy).
//
// Historia de dos bugs opuestos:
//
// 1. Al principio, CUALQUIER 401 se trataba como "sesión expirada" → toast +
//    logout + redirect, aunque fuera un 401 puntual de UN endpoint (permisos,
//    WAF, bug de backend) con la sesión perfectamente viva. El mount de
//    /admin/recolecciones dispara ~7 endpoints en ráfaga y uno solo que
//    devolviera 401 mataba toda la sesión.
//
// 2. El arreglo fue no tocar la sesión si el accessToken NO estaba vencido
//    según su `exp` local. Eso invirtió el problema: pasó a decidir el CLIENTE,
//    mirando un campo que él mismo no puede validar. Cuando el servidor empezó
//    a rechazar tokens que localmente seguían "vigentes" (revocación
//    server-side), el interceptor concluía "401 puntual" y dejaba al usuario
//    dentro del panel con absolutamente todo fallando, sin deslogueo y sin
//    forma de recuperarse salvo limpiar el storage a mano.
//
// AHORA decide el SERVIDOR, que es el único que sabe si la credencial sirve. El
// `exp` local no se consulta: ante un 401 se intenta renovar, y el desenlace de
// esa renovación es el veredicto.
//
//   200        → la sesión vivía; se reintenta el request original una vez. Si
//                vuelve a dar 401, ES puntual de ese endpoint → se propaga sin
//                tocar la sesión (que era la intención del arreglo #1).
//   401 / 403  → el servidor rechaza la credencial → sesión muerta → logout.
//   400        → también es un veredicto: el backend rechaza el canje (p. ej.
//                el usuario fue bloqueado con la sesión abierta) → logout. Si
//                el motivo es el bloqueo, se muestra ese mensaje en vez del
//                genérico de "sesión expirada".
//   5xx, 429,  → no hubo veredicto. Se propaga el error SIN tocar la sesión:
//   red          un problema de infraestructura no puede costarle las
//                credenciales a un usuario cuya sesión está viva.

/**
 * @param {Error} error - error 401 que disparó el cierre
 * @param {string} [message] - texto ya traducido para el toast; vacío → el
 *   texto por defecto de "sesión expirada"
 */
export function expireSession(error, message) {
  // Mismo cierre que el comportamiento histórico: toast deduplicado
  // (_sessionExpiredHandled en errorHandler) + logout + redirect.
  errorHandler.handleSessionExpired(
    error,
    () => {
      AuthService.logout();
      setTimeout(() => {
        // La sesión puede morir justo en la pantalla de autorización del
        // connect-flow: tras el nuevo login, el comercio vuelve a ella.
        rememberConnectReturn();
        window.location.replace("/login");
      }, 1500);
    },
    message
  );
}

// ¿Hay una sesión almacenada (aunque esté vencida)? Distinto de
// getAccessTokenApi(), que devuelve null también para tokens vencidos: acá
// preguntamos si el usuario alguna vez se logueó en este browser.
function hasStoredSession() {
  const at = localStorage.getItem(ACCESS_TOKEN);
  const rt = localStorage.getItem(REFRESH_TOKEN);
  return (at && at !== "null") || (rt && rt !== "null");
}

export function handleUnauthorized(error, axiosInstance) {
  const cfg = error.config;

  // Reintento post-refresh que volvió a fallar: propaga. Corta cualquier loop
  // (máximo 1 reintento por request).
  if (cfg?.__retriedAfterRefresh) {
    return Promise.reject(error);
  }

  // 401 pre-auth (sign-in con credenciales malas, recovery, etc.): no hay
  // sesión que expirar. Sin este guard, un login fallido disparaba el toast
  // "Tu sesión ha expirado" + reload de /login encima del error propio del
  // formulario de Login.
  if (!hasStoredSession()) {
    return Promise.reject(error);
  }

  // Carrera post-refresh: este request viajó con un token VIEJO porque otro 401
  // en paralelo ya refrescó y rotó el token antes de que llegara esta
  // respuesta. No hace falta gastar otro refresh: se reintenta directo, y el
  // request interceptor relee localStorage.
  //
  // Se compara contra el token CRUDO del storage y no contra
  // getAccessTokenApi(), que devuelve null si está localmente vencido: acá sólo
  // interesa si el valor cambió, no si el cliente lo cree válido.
  const stored = localStorage.getItem(ACCESS_TOKEN);
  const sentAuth = cfg?.headers?.Authorization;
  if (cfg && stored && stored !== "null" && sentAuth && sentAuth !== `Bearer ${stored}`) {
    cfg.__retriedAfterRefresh = true;
    return axiosInstance.request(cfg);
  }

  const refreshToken = AuthService.getRefreshTokenApi();
  if (!refreshToken) {
    // Sin refreshToken utilizable no hay rescate posible: sesión muerta.
    expireSession(error);
    return Promise.reject(error);
  }

  // `refreshSession` ya garantiza un solo canje en vuelo: N requests que den
  // 401 a la vez se enganchan a la misma promesa. El mutex vive allá y no acá
  // porque el interceptor no es el único que renueva.
  return AuthService.refreshSession(refreshToken).then(({ status, reason }) => {
    if (status === REFRESH_TERMINAL) {
      // El servidor rechazó la credencial: la sesión murió de verdad. Si el
      // motivo fue un bloqueo de cuenta (400 del backend), el usuario tiene
      // derecho a saber por qué quedó afuera.
      if (reason === "blocked") {
        expireSession(error, i18n.t("Tu cuenta fue bloqueada. Contacta a tu administrador."));
      } else {
        expireSession(error);
      }
      return Promise.reject(error);
    }

    if (status !== REFRESH_OK || !cfg) {
      // Transitorio, o request que no se puede reintentar. Se propaga el error
      // y la sesión queda intacta: puede rescatarse en el próximo intento.
      return Promise.reject(error);
    }

    cfg.__retriedAfterRefresh = true;
    return axiosInstance.request(cfg);
  });
}
